/**
 * Article Drawer front-end behaviour — the port of the three effects in `ArticleDrawer.tsx`.
 *
 * 1. OPEN / CLOSE. The rail fades out while the popover is open, the popover slides in from the
 *    left; Escape and a click outside both close it, and following a contents link closes it too
 *    (the reader asked to go somewhere). The class swaps below are exactly the conditional
 *    branches of the reference's `cn()` calls, so the two surfaces compute the same styles in
 *    both states.
 *
 * 2. SCROLL-SPY (R-SPY2). Position-based, NOT IntersectionObserver: the active section is the
 *    LAST heading whose top has crossed the reading line 120 px below the viewport top, which is
 *    deterministic when several headings are on screen at once - an observer reports whichever
 *    intersected last. Plus the end-of-page guard, without which the final section never lights
 *    up. rAF-throttled so a scroll does not recompute per pixel.
 *
 * 3. COPY LINK. Shared with the mobile author bar, and it answers on the label —
 *    `../article-shared/copy-link` carries the reasoning.
 *
 * 4. PHONE CONTENTS (T-102 step 3, contents B). Below lg a section bar under the header names the
 *    current section ("03/07 Section", from the scroll-spy above) and opens the same list as a
 *    modal sheet. `render.php` carries the reasoning; the bar ships `hidden` and is revealed
 *    here, so without JavaScript there is no bar.
 *
 * Everything here is enhancement: the rail, the popover and the contents list are all in the
 * server HTML, and the article itself needs none of it.
 */

import { initCopyLink } from '../article-shared/copy-link';

const READING_LINE = 120;
const PHONE_BAR_HEIGHT = 44;
const PAGE_BOTTOM_SLACK = 200;

const RAIL_OPEN = [ 'opacity-0', 'pointer-events-none' ];
const DASH_ACTIVE = [ 'w-2.5', 'bg-sage-70' ];
const DASH_IDLE = [ 'w-4', 'bg-[rgba(0,0,0,0.28)]' ];
const PANEL_OPEN = [ 'opacity-100', 'translate-x-0', 'pointer-events-auto' ];
const PANEL_CLOSED = [ 'opacity-0', '-translate-x-2', 'pointer-events-none' ];
const LINK_ACTIVE = [
	'bg-surface-muted',
	'text-grey-100',
	'font-medium',
	'border-sage-70',
];
const LINK_IDLE = [
	'text-grey-70',
	'hover:text-grey-100',
	'border-transparent',
	'hover:border-[rgba(0,0,0,0.12)]',
];

function initDrawer( root ) {
	const rail = root.querySelector( '[data-ac-drawer-rail]' );
	const panel = root.querySelector( '[data-ac-drawer-panel]' );
	if ( ! rail || ! panel ) {
		return;
	}

	const links = Array.from(
		panel.querySelectorAll( '[data-ac-drawer-link]' )
	);
	const ids = links.map( ( link ) => link.dataset.acDrawerLink );
	const openLabel = rail.getAttribute( 'aria-label' );
	const closeLabel = panel.dataset.closeRailLabel ?? openLabel;

	let isOpen = false;

	// The phone contents (T-102 step 3): the bar, its scrim and the sheet state.
	const phone = window.matchMedia( '(width < 64rem)' );
	const bar = root.querySelector( '[data-ac-drawer-bar]' );
	const scrim = root.querySelector( '[data-ac-drawer-scrim]' );
	const barCount = bar?.querySelector( '[data-ac-drawer-count]' );
	const barCurrent = bar?.querySelector( '[data-ac-drawer-current]' );
	const barProgress = bar?.querySelector( '[data-ac-drawer-progress]' );
	const contentsLabel = bar?.getAttribute( 'aria-label' ) ?? '';
	const panelLabel = panel.getAttribute( 'aria-label' ) ?? '';
	let sheetOpen = false;
	let activeId = ids[ 0 ];
	// Under the bar the reading line drops by the bar's height: a heading reached from the list
	// lands at 128 px (scroll-margin-top below lg) and must count as the current section.
	const readingLine = () =>
		bar && phone.matches ? READING_LINE + PHONE_BAR_HEIGHT : READING_LINE;

	const setOpen = ( next ) => {
		isOpen = next;
		rail.classList.toggle( RAIL_OPEN[ 0 ], next );
		rail.classList.toggle( RAIL_OPEN[ 1 ], next );
		rail.setAttribute( 'aria-expanded', next ? 'true' : 'false' );
		rail.setAttribute( 'aria-label', next ? closeLabel : openLabel );
		panel.setAttribute( 'aria-hidden', next ? 'false' : 'true' );
		PANEL_OPEN.forEach( ( c ) => panel.classList.toggle( c, next ) );
		PANEL_CLOSED.forEach( ( c ) => panel.classList.toggle( c, ! next ) );
	};

	// The sheet: the same list, lifted out of the page under the bar. Opening it locks the page
	// scroll and moves focus to the current section; Escape, the scrim, the bar, a tap outside
	// and a link all close it, and focus goes back to the bar unless the reader went elsewhere.
	const setSheet = ( next, { returnFocus = true } = {} ) => {
		if ( ! bar || next === sheetOpen ) {
			return;
		}
		sheetOpen = next;
		panel.classList.toggle( 'is-sheet', next );
		bar.setAttribute( 'aria-expanded', next ? 'true' : 'false' );
		bar.classList.toggle( 'is-open', next );
		if ( scrim ) {
			scrim.hidden = ! next;
		}
		document.documentElement.style.overflow = next ? 'hidden' : '';
		if ( next ) {
			panel.setAttribute( 'aria-modal', 'true' );
			panel.setAttribute( 'aria-label', contentsLabel );
			panel.setAttribute( 'aria-hidden', 'false' );
			const current =
				links.find(
					( link ) => link.dataset.acDrawerLink === activeId
				) ?? links[ 0 ];
			current?.focus( { preventScroll: true } );
			current?.scrollIntoView( { block: 'nearest' } );
			return;
		}
		panel.removeAttribute( 'aria-modal' );
		panel.setAttribute( 'aria-label', panelLabel );
		panel.setAttribute( 'aria-hidden', isOpen ? 'false' : 'true' );
		if ( returnFocus ) {
			bar.focus( { preventScroll: true } );
		}
	};

	rail.addEventListener( 'click', () => setOpen( ! isOpen ) );
	panel
		.querySelector( '[data-ac-drawer-close]' )
		?.addEventListener( 'click', () => setOpen( false ) );
	links.forEach( ( link ) =>
		link.addEventListener( 'click', () => {
			// Unlock the page before the browser follows the anchor, or the jump is swallowed.
			setSheet( false, { returnFocus: false } );
			setOpen( false );
		} )
	);

	if ( bar ) {
		bar.hidden = false;
		bar.addEventListener( 'click', () => setSheet( ! sheetOpen ) );
		scrim?.addEventListener( 'click', () => setSheet( false ) );
		// A tap anywhere outside the sheet and the bar (the header's menu toggle included)
		// closes it: two modal layers must never be open at once.
		document.addEventListener( 'pointerdown', ( event ) => {
			if (
				sheetOpen &&
				! panel.contains( event.target ) &&
				! bar.contains( event.target ) &&
				! scrim?.contains( event.target )
			) {
				setSheet( false, { returnFocus: false } );
			}
		} );
		document.addEventListener( 'keydown', ( event ) => {
			if ( ! sheetOpen ) {
				return;
			}
			if ( event.key === 'Escape' ) {
				setSheet( false );
				return;
			}
			if ( event.key !== 'Tab' ) {
				return;
			}
			// Keep Tab inside the bar and the list while the sheet is open.
			const ring = [ bar, ...links ];
			const at = ring.indexOf( root.ownerDocument.activeElement );
			const to = event.shiftKey
				? ( at <= 0 ? ring.length : at ) - 1
				: ( at + 1 ) % ring.length;
			event.preventDefault();
			ring[ to ].focus( { preventScroll: true } );
		} );
		// Widening past lg hides the bar by CSS; the sheet and its lock must not outlive it.
		phone.addEventListener( 'change', () =>
			setSheet( false, { returnFocus: false } )
		);
	}

	document.addEventListener( 'mousedown', ( event ) => {
		if ( ! isOpen ) {
			return;
		}
		if ( panel.contains( event.target ) || rail.contains( event.target ) ) {
			return;
		}
		setOpen( false );
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( isOpen && event.key === 'Escape' ) {
			setOpen( false );
		}
	} );

	if ( ids.length === 0 ) {
		return;
	}

	const dashes = Array.from( rail.querySelectorAll( 'span' ) );
	const paint = () => {
		dashes.forEach( ( dash, index ) => {
			const active = ids[ index ] === activeId;
			DASH_ACTIVE.forEach( ( c ) => dash.classList.toggle( c, active ) );
			DASH_IDLE.forEach( ( c ) => dash.classList.toggle( c, ! active ) );
			if ( active ) {
				dash.setAttribute( 'data-active', '' );
			} else {
				dash.removeAttribute( 'data-active' );
			}
		} );
		links.forEach( ( link ) => {
			const active = link.dataset.acDrawerLink === activeId;
			LINK_ACTIVE.forEach( ( c ) => link.classList.toggle( c, active ) );
			LINK_IDLE.forEach( ( c ) => link.classList.toggle( c, ! active ) );
			if ( active ) {
				link.setAttribute( 'aria-current', 'location' );
			} else {
				link.removeAttribute( 'aria-current' );
			}
		} );
		if ( bar ) {
			const index = Math.max( 0, ids.indexOf( activeId ) );
			const label = links[ index ]?.textContent.trim() ?? '';
			const pad = ( n ) => String( n ).padStart( 2, '0' );
			barCount.textContent = pad( index + 1 ) + '/' + pad( ids.length );
			barCurrent.textContent = label;
			bar.setAttribute(
				'aria-label',
				`${ contentsLabel }: ${ label }, ${ index + 1 } / ${
					ids.length
				}`
			);
		}
	};

	// The bar shows between the first section heading and the end of the article: over "More
	// research", the closing call and the footer it would only name the last section. Its
	// hairline fills with the share of the article body already scrolled past.
	const body = document.querySelector( '.ac-article-body' );
	const article = body?.closest( 'article' ) ?? body;
	const paintBar = () => {
		if ( ! bar ) {
			return;
		}
		const first = document.getElementById( ids[ 0 ] );
		const show =
			!! first &&
			first.getBoundingClientRect().top <= readingLine() &&
			( ! article ||
				article.getBoundingClientRect().bottom > readingLine() );
		bar.classList.toggle( 'is-visible', show || sheetOpen );
		if ( body && barProgress ) {
			const box = body.getBoundingClientRect();
			const span = Math.max( 1, box.height - window.innerHeight );
			const share = Math.min( 1, Math.max( 0, -box.top / span ) );
			barProgress.style.transform = `scaleX(${ share.toFixed( 4 ) })`;
		}
	};

	const computeActive = () => {
		const headings = ids
			.map( ( id ) => document.getElementById( id ) )
			.filter( Boolean );
		if ( headings.length === 0 ) {
			return;
		}

		let next = headings[ 0 ].id;
		for ( const heading of headings ) {
			if ( heading.getBoundingClientRect().top <= readingLine() ) {
				next = heading.id;
			} else {
				break;
			}
		}

		const scrollBottom = window.scrollY + window.innerHeight;
		const pageBottom =
			document.documentElement.scrollHeight - PAGE_BOTTOM_SLACK;
		if ( scrollBottom >= pageBottom ) {
			next = headings[ headings.length - 1 ].id;
		}

		if ( next !== activeId ) {
			activeId = next;
			paint();
		}
	};

	let rafId = 0;
	let dirty = false;
	const onScroll = () => {
		if ( dirty ) {
			return;
		}
		dirty = true;
		rafId = window.requestAnimationFrame( () => {
			dirty = false;
			window.cancelAnimationFrame( rafId );
			computeActive();
			paintBar();
		} );
	};

	computeActive();
	if ( bar ) {
		paint();
		paintBar();
	}
	window.addEventListener( 'scroll', onScroll, { passive: true } );
	window.addEventListener( 'resize', onScroll );
}

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.wp-block-avoid-content-article-drawer' )
		.forEach( ( root ) => {
			initDrawer( root );
			root.querySelectorAll( '[data-ac-copy-link]' ).forEach(
				initCopyLink
			);
		} );
} );
